/**
 * Argus Live — chat panel (left column of the 3-pane editor).
 *
 * AL-B3 v1: wires the chat to the existing Ask Argus
 * `POST /api/v1/ecosystem/query` SSE endpoint and surfaces an
 * `@argus-code` mention hint per the multi-agent-in-chat pattern (PR
 * #378). AL-B4 replaces the agent slot with the
 * `system.argus_live_author` agent — same SSE contract, different agent
 * id, so the swap is a one-line `targetAgentSlug` override and not a
 * rewrite of this component.
 *
 * Pattern matches `tool-developer/ToolDeveloperPage.tsx` (PR #58) and
 * the chat-driven Skill / Prompt / Function helper flows: a focused
 * left column with `MarkdownRenderer` bubbles + a `Textarea` composer.
 * We deliberately do NOT reuse `<ChatMessages>` from `ask-argus/` —
 * that component is wired to the full multi-conversation state machine
 * (sidebar, citations panel, confidence chips) and would drag in a
 * lot of context that doesn't belong in the dashboard authoring
 * sidebar.
 *
 * V1 honesty: the chat drafts dashboard code and binding JSON, but the
 * editor does not yet auto-apply model-emitted canvas mutations. The
 * user reviews output and applies it through the HTML/CSS/JS/BINDINGS
 * editor tabs.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, RotateCcw, Sparkles, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArgusEye } from '@/components/icons';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';

// ─── Local message shape ────────────────────────────────────────────────

export interface ArgusLiveChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  /** Markdown content. Always a string for both partial + final renders. */
  content: string;
  /** Stamped on send; rendered as a tooltip on the avatar. */
  timestamp: number;
}

let _msgCounter = 0;
function makeChatId(): string {
  return `al-msg-${++_msgCounter}-${Date.now()}`;
}

// ─── Welcome message ────────────────────────────────────────────────────

const WELCOME_MARKDOWN = `
I'm Argus. Tell me what dashboard you want to build and I'll help you draft it.

**Example prompts**

- _"Show me a list of all open work orders from CMMS, grouped by priority."_
- _"Build a KPI strip with deal count + total deal value from Zoho Bigin."_
- _"Add a chart showing equipment downtime over the last 30 days."_

Tag \`@argus-code\` in any message to invoke the Argus Code specialist for
deep TypeScript / HTML / CSS authoring.
`.trim();

// ─── Props ──────────────────────────────────────────────────────────────

export interface ArgusLiveChatPanelProps {
  /** Dashboard id — folded into the SSE request so the server can scope context. */
  dashboardId: string;
  /** Disable the composer (e.g. when the user lacks write permission). */
  disabled?: boolean;
  /** Optional banner above the messages (e.g. "Read-only view"). */
  bannerMarkdown?: string;
  /**
   * Optional override for the agent slug to address. AL-B3 leaves this
   * empty (Ask Argus default). AL-B4 will pass
   * `'system.argus_live_author'` once the author agent ships.
   */
  targetAgentSlug?: string;
  /**
   * Called every time a token is streamed in. V1 records the transcript;
   * auto-apply mutation cards are a later authoring upgrade.
   */
  onAssistantToken?: (text: string) => void;
}

// ─── Component ──────────────────────────────────────────────────────────

export function ArgusLiveChatPanel({
  dashboardId,
  disabled = false,
  bannerMarkdown,
  targetAgentSlug,
  onAssistantToken,
}: ArgusLiveChatPanelProps) {
  const [messages, setMessages] = useState<ArgusLiveChatMessage[]>(() => [
    {
      id: makeChatId(),
      role: 'assistant',
      content: WELCOME_MARKDOWN,
      timestamp: Date.now(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const tailRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest message
  useEffect(() => {
    tailRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setInput('');
    setMessages([
      {
        id: makeChatId(),
        role: 'assistant',
        content: WELCOME_MARKDOWN,
        timestamp: Date.now(),
      },
    ]);
  }, []);

  /**
   * R4 P1: surface an abort affordance for the in-flight stream.
   * Mirrors the Ask Argus PR #465 pattern (`ask-argus/chat-input.tsx`)
   * — when `isStreaming === true` the Send button is replaced with a
   * destructive Stop button that calls `abortRef.current.abort()`.
   *
   * The fetch-loop's `catch` branch already handles `AbortError`
   * silently (returns early), so cancelling mid-stream leaves the
   * partially-streamed bubble intact rather than overwriting it
   * with an error message.
   */
  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming || disabled) return;
    setInput('');

    const userMsg: ArgusLiveChatMessage = {
      id: makeChatId(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
    };
    const asstId = makeChatId();
    const asstMsg: ArgusLiveChatMessage = {
      id: asstId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg, asstMsg]);
    setIsStreaming(true);

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      // S3 P1 #7 fix: route the SSE call through apiClient.requestStream
      // so it benefits from the same 401 → refresh → retry loop as the
      // rest of the SPA. The previous raw `fetch('/api/v1/ecosystem/query', ...)`
      // pulled `accessToken` out of localStorage by hand, which meant
      // long-lived editor sessions silently lost chat on token expiry
      // (the user just saw a "Chat failed: HTTP 401" bubble and had to
      // reload the whole page to recover).
      //
      // `requestStream` is the SSE-aware sibling of `requestRaw`: it
      // returns the raw Response (so we still read frames from
      // `res.body.getReader()`) but transparently refreshes on 401 and
      // re-issues the request with the new bearer.
      const res = await apiClient.requestStream('/ecosystem/query', {
        method: 'POST',
        bodyJson: {
          query: text,
          conversationId: null,
          context: {
            surface: 'argus_live_author',
            dashboardId,
            targetAgentSlug: targetAgentSlug ?? null,
          },
        },
        signal: ac.signal,
      });

      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${errText.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE frames are separated by double newlines per the spec
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const frame of events) {
          const dataLine = frame
            .split('\n')
            .find((l) => l.startsWith('data: '));
          if (!dataLine) continue;
          const json = dataLine.slice(6).trim();
          if (!json) continue;
          try {
            const payload = JSON.parse(json) as {
              type: string;
              content?: string;
              message?: string;
              code?: string;
              data?: { content?: string };
            };
            if (payload.type === 'token' && typeof payload.content === 'string') {
              const piece = payload.content;
              onAssistantToken?.(piece);
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.id === asstId) {
                  next[next.length - 1] = {
                    ...last,
                    content: last.content + piece,
                  };
                }
                return next;
              });
            } else if (
              payload.type === 'token' &&
              typeof payload.data?.content === 'string'
            ) {
              const piece = payload.data.content;
              onAssistantToken?.(piece);
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.id === asstId) {
                  next[next.length - 1] = {
                    ...last,
                    content: last.content + piece,
                  };
                }
                return next;
              });
            } else if (payload.type === 'error') {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.id === asstId) {
                  next[next.length - 1] = {
                    ...last,
                    content:
                      last.content +
                      `\n\n_Error: ${payload.message ?? payload.code ?? 'unknown'}_`,
                  };
                }
                return next;
              });
            }
          } catch {
            // Bad SSE frame — log defensively and keep going.
            // eslint-disable-next-line no-console
            console.warn('argus-live chat: bad SSE payload', json);
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.id === asstId && last.content === '') {
          next[next.length - 1] = {
            ...last,
            content: `_Chat failed: ${(err as Error).message}_`,
          };
        }
        return next;
      });
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [input, isStreaming, disabled, dashboardId, targetAgentSlug, onAssistantToken]);

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0">
      {/* Header */}
      {/*
          R4 P1: every horizontal flex item under this header gets
          `min-w-0` so the intrinsic header content (label + badge +
          reset button) doesn't override the parent ResizablePanel's
          `defaultSize={28}`. Without these, react-resizable-panels
          starved this pane down to ~45px because the header asked
          for more horizontal space than 28% of a 1920px viewport
          (~537px) allowed once the other 2 panels claimed their
          share.
       */}
      <div className="border-b border-border/40 px-3 py-2 flex items-center justify-between shrink-0 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <ArgusEye state="idle" className="h-5 w-5 text-primary shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold truncate">
              Argus Live Author
            </span>
            <span className="text-[10px] text-muted-foreground truncate">
              Chat-driven dashboard authoring
            </span>
          </div>
          {targetAgentSlug ? null : (
            <Badge
              variant="outline"
              className="text-[9px] border-amber-500/30 text-amber-700 dark:text-amber-400 shrink-0"
              title="AL-B4 will swap this for the dedicated argus_live_author agent."
            >
              preview
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={reset}
          title="Reset conversation"
          aria-label="Reset conversation"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Banner */}
      {bannerMarkdown && (
        <div className="px-3 py-2 border-b bg-amber-500/5 text-[11px] text-amber-700 dark:text-amber-400 leading-snug shrink-0">
          <MarkdownRenderer content={bannerMarkdown} />
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-auto p-3 space-y-3"
        data-testid="argus-live-chat-messages"
      >
        {messages.map((msg) => {
          if (msg.role === 'assistant') {
            return (
              <div key={msg.id} className="flex gap-2.5">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <ArgusEye state="idle" className="h-5 w-5 text-primary" />
                </div>
                <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3 min-w-0 max-w-[88%]">
                  {msg.content ? (
                    <MarkdownRenderer content={msg.content} />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 animate-pulse text-muted-foreground" />
                  )}
                </div>
              </div>
            );
          }
          if (msg.role === 'user') {
            return (
              <div key={msg.id} className="flex justify-end">
                <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm max-w-[85%]">
                  {msg.content}
                </div>
              </div>
            );
          }
          return null;
        })}
        {isStreaming && (
          <div className="flex gap-2.5">
            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <ArgusEye state="thinking" blink="fast" className="h-5 w-5 text-primary" />
            </div>
            <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" />
              <span
                className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce"
                style={{ animationDelay: '150ms' }}
              />
              <span
                className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce"
                style={{ animationDelay: '300ms' }}
              />
            </div>
          </div>
        )}
        <div ref={tailRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-border/40 p-3 shrink-0">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder={
              disabled
                ? 'You have view-only access to this dashboard.'
                : 'Describe what you want to build…'
            }
            className={cn(
              'min-h-[40px] max-h-[120px] resize-none text-sm',
              disabled && 'opacity-60',
            )}
            disabled={disabled || isStreaming}
            rows={1}
            aria-label="Chat message input"
          />
          {/*
             R4 P1: while a stream is in flight, replace the Send
             button with a destructive Stop button (mirrors Ask Argus
             PR #465 — `ask-argus/chat-input.tsx`). The textarea stays
             disabled either way; only the affordance changes.
           */}
          {isStreaming ? (
            <Button
              onClick={handleStop}
              variant="destructive"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-xl"
              aria-label="Stop generating"
              title="Stop generating"
              data-testid="argus-live-chat-stop-button"
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={() => void send()}
              disabled={disabled || !input.trim()}
              size="icon"
              className="h-10 w-10 shrink-0 rounded-xl"
              aria-label="Send message"
              title="Send (Enter)"
              data-testid="argus-live-chat-send-button"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
